/**
 * Receive: the live tail of inbound HL7 and the ACKs we returned.
 *
 * This is the MedStar acceptance criterion. Everything else on the tab supports
 * it.
 *
 * Deliberately absent, and not an oversight: Start/Stop buttons (a UI able to
 * drop a channel mid-window is a liability), a message log file (nothing is
 * written to disk), an ACK on/off toggle and an MSH-delimiter toggle (both are
 * always on). See `docs/specs/reference-ui-to-api-map.md` in integrations-service.
 */
import { useState } from 'react'
import { Eraser, Pause, Play, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { dataLayerApi } from '@/api/dataLayerApi'
import { countBadMessages } from '@/lib/dataLayer/counters'
import { inboundChannels } from '@/lib/dataLayer/channelView'
import { useDataLayerTail } from '@/hooks/useDataLayerTail'
import type { DataLayerFailure } from '@/lib/dataLayer/errors'
import type { Hl7Channel } from '@/lib/dataLayer/types'
import { InjectMessageDialog } from './InjectMessageDialog'
import { MessageDetailDialog } from './MessageDetailDialog'
import { PacketLog } from './PacketLog'

const ALL = '__all__'
const TYPES = ['ADT', 'ORM', 'ORU']

export function ReceivePanel({ channels }: { channels: Hl7Channel[] }) {
  const { can } = usePermission()
  // The proxy routes are @AuthOnly on the backend (any signed-in user), so
  // this gate is UI policy that is deliberately STRICTER than enforcement — a
  // departure from the repo's usual "gate each control by the route it calls",
  // and worth knowing before anyone reads it as a security boundary.
  const canManage = can(PERMISSIONS.integrationsManage)

  const [channel, setChannel] = useState(ALL)
  const [type, setType] = useState(ALL)
  const [paused, setPaused] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [injectOpen, setInjectOpen] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const [clearFailure, setClearFailure] = useState<DataLayerFailure | null>(null)

  const inbound = inboundChannels(channels)

  const { tail, isLoading, clear, reload } = useDataLayerTail(
    {
      direction: 'in',
      channel: channel === ALL ? undefined : channel,
      type: type === ALL ? undefined : type,
    },
    !paused,
  )

  const badMessages = countBadMessages(tail.messages)

  async function doClear() {
    setClearFailure(null)
    const outcome = await dataLayerApi.clearMessages()
    setConfirmClear(false)
    if (!outcome.ok) {
      setClearFailure(outcome.failure)
      return
    }
    clear()
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All inbound channels</SelectItem>
            {inbound.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.id} · :{c.port}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)}>
          {paused ? <Play className="mr-2 h-3.5 w-3.5" /> : <Pause className="mr-2 h-3.5 w-3.5" />}
          {paused ? 'Resume' : 'Pause'}
        </Button>

        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <>
              <Button size="sm" onClick={() => setInjectOpen(true)}>
                <Zap className="mr-2 h-3.5 w-3.5" />
                Inject test message
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirmClear(true)}>
                <Eraser className="mr-2 h-3.5 w-3.5" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>

      {paused && (
        <p className="text-xs text-muted-foreground">
          Paused. New packets are still being received and stored; the log will catch up on resume.
        </p>
      )}

      {tail.restarted && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700">
          The data layer restarted, so the log was reset. Its in-memory buffer does not survive a
          restart.
        </p>
      )}

      {tail.missed > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            {tail.missed} packet{tail.missed === 1 ? '' : 's'} not shown. More arrived at once than a
            page carries, and they are still in the data layer's buffer.
          </span>
          <Button variant="outline" size="sm" onClick={reload}>
            Reload log
          </Button>
        </div>
      )}

      {clearFailure && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {clearFailure.message}
        </p>
      )}

      {/* The Iguana Listener's "Bad Messages" figure. No endpoint returns it, so
          it is derived from the rows on screen, and the wording says so: this
          counts the visible log, not the life of the channel. Anything stronger
          would be false the moment someone clears or filters. */}
      {tail.messages.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <span className={badMessages > 0 ? 'font-medium text-amber-700' : undefined}>
            Bad messages {badMessages}
          </span>{' '}
          · in the {tail.messages.length} packet{tail.messages.length === 1 ? '' : 's'} shown
        </p>
      )}

      <PacketLog
        messages={tail.messages}
        isLoading={isLoading}
        selectedId={selectedId}
        onSelect={(message) => setSelectedId(message.id)}
        emptyHint={
          canManage ? (
            <>
              No packets yet. Use <span className="font-medium text-foreground">Inject test message</span>{' '}
              to send one through the real receive path.
            </>
          ) : (
            'No packets yet.'
          )
        }
      />

      <InjectMessageDialog
        open={injectOpen}
        onOpenChange={setInjectOpen}
        channels={inbound}
        onInjected={reload}
      />

      <MessageDetailDialog messageId={selectedId} onClose={() => setSelectedId(null)} />

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Clear all packets</DialogTitle>
            {/* The store is one global buffer, so this is not scoped to the
                filter or even to this direction. Saying so prevents it reading
                as data loss when the Send log empties too. */}
            <DialogDescription>
              This empties the whole packet buffer, <strong>both received and sent</strong>, not just
              what is filtered here. Channel counters are left alone, so "Received" keeps its value.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClear(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={doClear}>
              Clear packets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
